
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Plus } from "lucide-react";
import { useCreateTimeSlot } from "@/hooks/useTimeSlots";
import { Service } from "@/hooks/useServices";
import { Room } from "@/hooks/useRooms";
import { useToast } from "@/hooks/use-toast";

interface AddTimeSlotDialogProps {
  providerId: string;
  services: Service[];
  rooms: Room[];
  onTimeSlotAdded?: () => void;
}

interface TimeSlotFormData {
  service_id: string;
  room_id?: string;
  date: string;
  start_time: string;
  end_time: string;
  max_bookings: number;
}

const AddTimeSlotDialog = ({ providerId, services, rooms, onTimeSlotAdded }: AddTimeSlotDialogProps) => {
  const [open, setOpen] = useState(false);
  const createTimeSlot = useCreateTimeSlot();
  const { toast } = useToast();
  const form = useForm<TimeSlotFormData>({
    defaultValues: {
      max_bookings: 1,
    },
  });

  const onSubmit = async (data: TimeSlotFormData) => {
    try {
      const startDateTime = new Date(`${data.date}T${data.start_time}`).toISOString();
      const endDateTime = new Date(`${data.date}T${data.end_time}`).toISOString();

      await createTimeSlot.mutateAsync({
        service_id: data.service_id,
        provider_id: providerId,
        room_id: data.room_id || null,
        start_time: startDateTime,
        end_time: endDateTime,
        max_bookings: data.max_bookings,
        current_bookings: 0,
        is_available: true,
        womens_only: false,
      });

      form.reset();
      setOpen(false);
      onTimeSlotAdded?.();
    } catch (error: any) {
      if (error?.code === '23505') {
        toast({
          title: "Already exists",
          description: "This time slot already exists for the selected service, room, and time.",
        });
      } else {
        console.error('Error creating time slot:', error);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Time Slot
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Time Slot</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="service_id"
              rules={{ required: "Please select a service" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="room_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Room (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a room" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {rooms.map((room) => (
                        <SelectItem key={room.id} value={room.id}>
                          {room.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="date"
              rules={{ required: "Please select a date" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_time"
                rules={{ required: "Please select start time" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Time</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_time"
                rules={{ required: "Please select end time" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Time</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="max_bookings"
              rules={{ 
                required: "Please enter max bookings",
                min: { value: 1, message: "Must be at least 1" }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Max Bookings</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="1" 
                      {...field} 
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createTimeSlot.isPending}>
                {createTimeSlot.isPending ? "Creating..." : "Create Time Slot"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddTimeSlotDialog;
